/**
 * 图片加载完成后触发重绘。
 *
 * Canvas 是一次性绘制的：图元里的图片还在下载时只能先画占位框，
 * 等图片到位必须主动重画一次，否则课件会一直停在虚线框上 ——
 * 这个坑在实机验证时才暴露出来。
 */

import { useEffect, useState } from 'react'

import { svgImageFor } from './svgAsset'
import { imageFor } from './wbRender'
import type { WBObject } from './whiteboard-types'

/**
 * 监听给定图元里尚未加载完的图片，返回一个每次加载完成就变化的计数，
 * 把它放进绘制 effect 的依赖里即可触发重绘。
 */
export function useImageRepaint(objects: WBObject[] | undefined): number {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!objects?.length) return

    const cleanups: Array<() => void> = []
    let alive = true
    for (const o of objects) {
      if (!o.blobUrl) continue

      // 矢量图不是 <img src> 直接加载的：要先 fetch 源码再消毒，
      // 是一条异步链，没有 img 的 load 事件可以监听。
      // 它自带回调，加载完调一次就行。
      if (o.kind === 'svg') {
        if (svgImageFor(o.blobUrl, () => { if (alive) setTick((n) => n + 1) })) continue
        continue
      }

      const img = imageFor(o.blobUrl)
      if (!img || img.complete) continue

      const bump = () => setTick((n) => n + 1)
      img.addEventListener('load', bump)
      img.addEventListener('error', bump)
      cleanups.push(() => {
        img.removeEventListener('load', bump)
        img.removeEventListener('error', bump)
      })
    }
    return () => {
      alive = false
      cleanups.forEach((f) => f())
    }
  }, [objects])

  return tick
}
